import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useApi } from '../hooks/useApi';
import { Card, Button, Grid, LoadingSpinner, StatCard, JobCard } from '../components/ui';

const COLORS = ['#92400e', '#b45309', '#d97706', '#f59e0b', '#fbbf24', '#fcd34d', '#fde68a'];

const Insights = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const [activeTab, setActiveTab] = useState('distribution');
  const [selectedJob, setSelectedJob] = useState(jobId);
  const [selectedVersion, setSelectedVersion] = useState('current');

  // Sync selectedJob with URL parameter
  React.useEffect(() => {
    if (jobId) {
      setSelectedJob(jobId);
      setSelectedVersion('current'); // Reset version when job changes
    }
    else {
      setSelectedJob(null);
    }
  }, [jobId]);

  // Correlation state
  const [xField, setXField] = useState('');
  const [yField, setYField] = useState('');
  const [aggregation, setAggregation] = useState('count');

  // Distribution state
  const [distField, setDistField] = useState('');
  const [topN, setTopN] = useState(10);

  // Trends state
  const [dateField, setDateField] = useState('');
  const [valueField, setValueField] = useState('');
  const [granularity, setGranularity] = useState('day');

  // Fetch all jobs
  const { data: jobsData } = useQuery({
    queryKey: ['jobs'],
    queryFn: async () => {
      const response = await api.get('/api/jobs');
      return response.data;
    },
  });

  const jobs = jobsData?.jobs || [];
  const completedJobs = jobs.filter((job) => job.status === 'completed');

  // Fetch versions for selected job
  const { data: versionsData } = useQuery({
    queryKey: ['versions', selectedJob],
    queryFn: async () => {
      const response = await api.get(`/api/data/${selectedJob}/versions`);
      return response.data;
    },
    enabled: !!selectedJob,
  });

  const versions = versionsData?.versions || [];

  // Fetch job data (current or specific version)
  const { data: jobData, isLoading, error } = useQuery({
    queryKey: ['jobData', selectedJob, selectedVersion],
    queryFn: async () => {
      if (selectedVersion === 'current') {
        const response = await api.get(`/api/data/${selectedJob}`);
        return response.data;
      } else {
        // Fetch version snapshot
        const response = await api.get(`/api/data/${selectedJob}/versions/${selectedVersion}`);
        return response.data;
      }
    },
    enabled: !!selectedJob,
  });

  // Initialize fields when data loads
  React.useEffect(() => {
    if (jobData?.columns && jobData.columns.length > 0) {
      if (!xField) setXField(jobData.columns[0]);
      if (!yField && jobData.columns.length > 1) setYField(jobData.columns[1]);
      if (!distField) setDistField(jobData.columns[0]);
      if (!dateField) setDateField(jobData.columns[0]);
      if (!valueField && jobData.columns.length > 1) setValueField(jobData.columns[1]);
    }
  }, [jobData?.columns, xField, yField, distField, dateField, valueField]);

  // Correlation analysis
  const correlationData = useMemo(() => {
    if (!jobData?.rows || !xField || !yField) return [];

    const grouped = {};
    jobData.rows.forEach((row) => {
      const x = row[xField] || 'N/A';
      const y = row[yField] || 'N/A';
      const key = aggregation === 'count' ? `${x} - ${y}` : `${x}`;

      if (!grouped[key]) {
        grouped[key] = { name: key, values: [] };
      }

      if (aggregation === 'count') {
        grouped[key].values.push(1);
      } else if (aggregation === 'sum' && !isNaN(parseFloat(y))) {
        grouped[key].values.push(parseFloat(y));
      }
    });

    return Object.values(grouped).map((item) => ({
      name: item.name,
      value: aggregation === 'count'
        ? item.values.length
        : item.values.reduce((a, b) => a + b, 0),
    })).sort((a, b) => b.value - a.value).slice(0, 15);
  }, [jobData?.rows, xField, yField, aggregation]);

  // Distribution analysis
  const distributionData = useMemo(() => {
    if (!jobData?.rows || !distField) return [];

    const counts = {};
    jobData.rows.forEach((row) => {
      const value = row[distField] || 'N/A';
      counts[value] = (counts[value] || 0) + 1;
    });

    return Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, topN);
  }, [jobData?.rows, distField, topN]);

  // Trends analysis
  const trendsData = useMemo(() => {
    if (!jobData?.rows || !dateField || !valueField) return [];

    const parseDate = (dateStr) => {
      if (!dateStr) return null;

      // Try parsing as-is (handles ISO format and most standard formats)
      let date = new Date(dateStr);
      if (!isNaN(date.getTime())) return date;

      // Try common formats: MM/DD/YYYY, DD/MM/YYYY, YYYY-MM-DD
      const formats = [
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, // MM/DD/YYYY or DD/MM/YYYY
        /^(\d{4})-(\d{1,2})-(\d{1,2})$/, // YYYY-MM-DD
        /^(\d{1,2})-(\d{1,2})-(\d{4})$/, // MM-DD-YYYY or DD-MM-YYYY
      ];

      for (const format of formats) {
        const match = String(dateStr).match(format);
        if (match) {
          // Try US format first (MM/DD/YYYY)
          date = new Date(match[1], match[2] - 1, match[3]);
          if (!isNaN(date.getTime())) return date;

          // Try EU format (DD/MM/YYYY)
          date = new Date(match[3], match[1] - 1, match[2]);
          if (!isNaN(date.getTime())) return date;
        }
      }

      return null;
    };

    const formatDate = (date, gran) => {
      if (gran === 'year') return date.getFullYear().toString();
      if (gran === 'month') return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      return date.toISOString().slice(0, 10);
    };

    const grouped = {};
    let validDateCount = 0;

    jobData.rows.forEach((row) => {
      const date = parseDate(row[dateField]);
      if (!date) return;

      validDateCount++;
      const key = formatDate(date, granularity);
      const value = parseFloat(row[valueField]) || 0;

      if (!grouped[key]) {
        grouped[key] = { date: key, values: [] };
      }
      grouped[key].values.push(value);
    });

    // If less than 10% of rows have valid dates, the field probably isn't a date field
    if (validDateCount < jobData.rows.length * 0.1) {
      console.warn(`Trends: Only ${validDateCount}/${jobData.rows.length} rows have valid dates in field "${dateField}"`);
    }

    return Object.values(grouped)
      .map((item) => ({
        date: item.date,
        average: item.values.reduce((a, b) => a + b, 0) / item.values.length,
        total: item.values.reduce((a, b) => a + b, 0),
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [jobData?.rows, dateField, valueField, granularity]);

  // Statistics
  const statistics = useMemo(() => {
    if (!jobData?.rows || !jobData?.columns) return null;

    const totalRows = jobData.rows.length;
    const fieldStats = {};

    jobData.columns.forEach((col) => {
      const values = jobData.rows.map((row) => row[col]).filter((v) => v != null && v !== '');
      const uniqueValues = new Set(values);
      const numericValues = values.map((v) => parseFloat(v)).filter((v) => !isNaN(v));

      fieldStats[col] = {
        total: values.length,
        unique: uniqueValues.size,
        nullCount: totalRows - values.length,
        nullPercentage: ((totalRows - values.length) / totalRows * 100).toFixed(1),
      };

      if (numericValues.length > 0) {
        const sum = numericValues.reduce((a, b) => a + b, 0);
        fieldStats[col].mean = (sum / numericValues.length).toFixed(2);
        fieldStats[col].min = Math.min(...numericValues);
        fieldStats[col].max = Math.max(...numericValues);
      }
    });

    return {
      totalRows,
      totalColumns: jobData.columns.length,
      fieldStats,
    };
  }, [jobData?.rows, jobData?.columns]);

  // No job selected - show job selector
  if (!selectedJob) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-primary dark:text-primary-light mb-2">
            Data Insights
          </h1>
          <p className="text-text-secondary-light dark:text-secondary">
            Select a completed job to analyze data
          </p>
        </div>

        <Card>
          <h2 className="text-xl font-semibold text-text-primary-light dark:text-primary mb-4">
            Select a Job
          </h2>
          {completedJobs.length > 0 ? (
            <div className="space-y-3">
              {completedJobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  onClick={() => navigate(`/insights/${job.id}`)}
                  showActions={false}
                />
              ))}
            </div>
          ) : (
            <p className="text-text-secondary-light dark:text-secondary text-center py-8">
              No completed jobs available. Process some images first.
            </p>
          )}
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <p className="text-red-600 dark:text-red-400 mb-4">
          Error loading data: {error.message}
        </p>
        <Button onClick={() => navigate('/insights')}>Back to Job Selection</Button>
      </div>
    );
  }

  if (!jobData?.rows || jobData.rows.length === 0) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-600 dark:text-gray-400 mb-4">No data available for analysis.</p>
        <Button onClick={() => navigate('/insights')}>Back to Job Selection</Button>
      </div>
    );
  }

  const tabs = [
    // { id: 'correlation', label: 'Correlation' },
    { id: 'distribution', label: 'Distribution' },
    // { id: 'trends', label: 'Trends' },
    { id: 'statistics', label: 'Statistics' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-primary dark:text-primary-light">
            Data Insights
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Analyze your extracted data
            {selectedVersion !== 'current' && (() => {
              const ver = versions.find(v => v.version_number === parseInt(selectedVersion));
              const displayName = ver?.label || `v${selectedVersion}`;
              return (
                <span className="ml-2 text-amber-600 dark:text-amber-400 font-medium">
                  (Viewing {displayName})
                </span>
              );
            })()}
          </p>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          <select
            value={selectedJob}
            onChange={(e) => navigate(`/insights/${e.target.value}`)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none dark:bg-surface-dark dark:text-white"
          >
            {completedJobs.map((job) => (
              <option key={job.id} value={job.id}>
                {job.name} - {new Date(job.created_at).toLocaleDateString()}
              </option>
            ))}
          </select>
          <select
            value={selectedVersion}
            onChange={(e) => setSelectedVersion(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none dark:bg-surface-dark dark:text-white"
          >
            <option value="current">Current Data</option>
            {versions.map((version) => (
              <option key={version.version_number} value={version.version_number}>
                {version.label || `v${version.version_number}`}
                {version.label && ` (v${version.version_number})`}
                {version.change_summary?.initial_version ? ' - Original' : ''}
                {' - '}
                {new Date(version.created_at).toLocaleDateString()}
              </option>
            ))}
          </select>
          <Button onClick={() => navigate(`/table/${selectedJob}`)} variant="secondary">
            View Table
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary dark:text-primary-dark'
                  : 'border-transparent text-text-secondary-light hover:text-text-primary-light hover:border-border-light dark:text-secondary dark:hover:text-primary'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'correlation' && (
        <Card>
          <h2 className="text-xl font-semibold text-text-primary-light dark:text-primary mb-4">
            Correlation Analysis
          </h2>
          <Grid cols={3} gap={4} className="mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                X Field
              </label>
              <select
                value={xField}
                onChange={(e) => setXField(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none dark:bg-surface-dark dark:text-white"
              >
                {jobData.columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Y Field
              </label>
              <select
                value={yField}
                onChange={(e) => setYField(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none dark:bg-surface-dark dark:text-white"
              >
                {jobData.columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Aggregation
              </label>
              <select
                value={aggregation}
                onChange={(e) => setAggregation(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none dark:bg-surface-dark dark:text-white"
              >
                <option value="count">Count</option>
                <option value="sum">Sum</option>
              </select>
            </div>
          </Grid>
          {correlationData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={correlationData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#d97706" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              <div className="text-center">
                <p className="text-lg font-medium">No data to display</p>
                <p className="text-sm mt-2">Try selecting different fields or check if your data contains valid values.</p>
              </div>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'distribution' && (
        <Card>
          <h2 className="text-xl font-semibold text-text-primary-light dark:text-primary mb-4">
            Distribution Analysis
          </h2>
          <Grid cols={2} gap={4} className="mb-6">
            <div>
              <label className="block text-sm font-medium mb-1">
                Field
              </label>
              <select
                value={distField}
                onChange={(e) => setDistField(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none dark:bg-surface-dark dark:text-white"
              >
                {jobData.columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Top N
              </label>
              <input
                type="number"
                value={topN}
                onChange={(e) => setTopN(parseInt(e.target.value) || 10)}
                min="1"
                max="50"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none dark:bg-surface-dark dark:text-white"
              />
            </div>
          </Grid>
          {distributionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={distributionData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                >
                  {distributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              <div className="text-center">
                <p className="text-lg font-medium">No data to display</p>
                <p className="text-sm mt-2">The selected field doesn't contain any values to visualize.</p>
              </div>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'trends' && (
        <Card>
          <h2 className="text-xl font-semibold text-text-primary-light dark:text-primary mb-4">
            Trends Analysis
          </h2>
          <Grid cols={3} gap={4} className="mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date Field
              </label>
              <select
                value={dateField}
                onChange={(e) => setDateField(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none dark:bg-surface-dark dark:text-white"
              >
                {jobData.columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Value Field
              </label>
              <select
                value={valueField}
                onChange={(e) => setValueField(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none dark:bg-surface-dark dark:text-white"
              >
                {jobData.columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Granularity
              </label>
              <select
                value={granularity}
                onChange={(e) => setGranularity(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none dark:bg-surface-dark dark:text-white"
              >
                <option value="day">Day</option>
                <option value="month">Month</option>
                <option value="year">Year</option>
              </select>
            </div>
          </Grid>
          {trendsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={trendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="average" stroke="#d97706" strokeWidth={2} name="Average" />
                <Line type="monotone" dataKey="total" stroke="#92400e" strokeWidth={2} name="Total" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              <div className="text-center">
                <p className="text-lg font-medium">No data to display</p>
                <p className="text-sm mt-2">The selected date field doesn't contain valid date values. Check the browser console for details.</p>
              </div>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'statistics' && statistics && (
        <div className="space-y-6">
          <Grid cols={2} gap={4}>
            <StatCard
              title="Rows"
              value={statistics.totalRows}
            />
            <StatCard
              title="Columns"
              value={statistics.totalColumns}
            />
          </Grid>

          <Card>
            <h2 className="text-xl font-semibold text-text-primary-light dark:text-primary mb-4">
              Column Statistics
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Column
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Values
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Unique
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Null Count
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Null %
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Mean
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Min
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Max
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {Object.entries(statistics.fieldStats).map(([column, stats]) => (
                    <tr key={column} className="hover:bg-surface-hover dark:hover:bg-surface-dark">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {column}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {stats.total}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {stats.unique}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {stats.nullCount}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {stats.nullPercentage}%
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {stats.mean || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {stats.min ?? '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {stats.max ?? '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card>
            <h2 className="text-xl font-semibold text-text-primary-light dark:text-primary mb-4">
              Data Quality Metrics
            </h2>
            <Grid cols={2} gap={4}>
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Completeness
                </h3>
                <p className="text-2xl font-bold text-primary dark:text-primary-dark">
                  {(
                    (Object.values(statistics.fieldStats).reduce((sum, stat) => sum + stat.total, 0) /
                      (statistics.totalRows * statistics.totalColumns)) *
                    100
                  ).toFixed(1)}%
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Average Uniqueness
                </h3>
                <p className="text-2xl font-bold text-primary dark:text-primary-dark">
                  {(
                    Object.values(statistics.fieldStats).reduce((sum, stat) => sum + (stat.unique / stat.total || 0), 0) /
                    Object.keys(statistics.fieldStats).length *
                    100
                  ).toFixed(1)}%
                </p>
              </div>
            </Grid>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Insights;
